'use client';

import { useState, useEffect } from 'react';
import { detectRegion, saveConsent, getConsent, logConsentAction } from '@/lib/consent';
import ConsentPreferenceCenter from './ConsentPreferenceCenter';

export default function CookieBanner() {
  const [region, setRegion] = useState<'gdpr' | 'ccpa' | 'none'>('none');
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    const detectedRegion = detectRegion();
    setRegion(detectedRegion);

    const existingConsent = getConsent();
    if (!existingConsent && detectedRegion === 'gdpr') {
      setShowBanner(true);
    }

    // Listen for cookie preferences event from footer
    const handleOpenPreferences = () => {
      setShowPreferences(true);
    };

    window.addEventListener('openCookiePreferences', handleOpenPreferences);
    return () => {
      window.removeEventListener('openCookiePreferences', handleOpenPreferences);
    };
  }, []);

  const handleAcceptAll = () => {
    saveConsent(
      {
        necessary: true,
        analytics: true,
        marketing: true,
        preferences: true
      },
      region
    );
    logConsentAction('accept_all', { region });
    setShowBanner(false);
  };

  const handleRejectOptional = () => {
    saveConsent(
      {
        necessary: true,
        analytics: false,
        marketing: false,
        preferences: false
      },
      region
    );
    logConsentAction('reject_optional', { region });
    setShowBanner(false);
  };

  const handleOpenPreferences = () => {
    setShowPreferences(true);
  };

  const handleClosePreferences = () => {
    setShowPreferences(false);
  };

  if (region === 'none' && !showPreferences) {
    return null;
  }

  return (
    <>
      {/* GDPR Banner */}
      {region === 'gdpr' && showBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-brand-primary text-white p-6 shadow-2xl">
          <div className="container mx-auto max-w-4xl">
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-3">
                Používáme cookies, aby web fungoval a dával smysl.
              </h2>
              <p className="text-white/90 mb-4">
                Pomáhají nám pochopit, co na webu funguje, a zobrazovat relevantní obsah. Můžeš si vybrat, co dovolíš.
              </p>
              <p className="text-sm text-white/70">
                Více informací najdeš v našich{' '}
                <a href="/privacy" className="underline hover:text-accent transition-colors">
                  Zásadách ochrany osobních údajů
                </a>
                {' '}a{' '}
                <a href="/zasady-cookies" className="underline hover:text-accent transition-colors">
                  Zásadách cookies
                </a>
                .
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={handleAcceptAll}
                className="px-6 py-3 bg-accent text-brand-primary font-semibold rounded hover:bg-accent/90 transition-colors"
              >
                Přijmout vše
              </button>
              <button
                onClick={handleRejectOptional}
                className="px-6 py-3 bg-white/20 text-white font-semibold rounded hover:bg-white/30 transition-colors border border-white/30"
              >
                Odmítnout nepovinné
              </button>
              <button
                onClick={handleOpenPreferences}
                className="px-6 py-3 bg-white/20 text-white font-semibold rounded hover:bg-white/30 transition-colors border border-white/30"
              >
                Nastavení
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preference Center Modal */}
      {showPreferences && (
        <ConsentPreferenceCenter
          region={region}
          onClose={handleClosePreferences}
        />
      )}
    </>
  );
}
